import { Link } from 'react-router-dom';
import { ArrowLeft, Users, AlertTriangle, Check, X } from 'lucide-react';

export default function CommunityRulesPage() {
  return (
    <div className="min-h-screen py-12" data-testid="community-rules-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Users className="h-8 w-8 text-primary" />
            <h1 
              className="text-3xl font-bold"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Community Rules
            </h1>
          </div>
          <p className="text-muted-foreground">
            Guidelines for a safe and respectful marketplace
          </p>
        </div>

        {/* Intro */}
        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 mb-8">
          <p className="text-muted-foreground leading-relaxed">
            HabeshaTown is built on trust and community. These rules help ensure a positive experience 
            for all members. By using our platform, you agree to follow these guidelines.
          </p>
        </div>

        {/* Rules */}
        <div className="space-y-6">
          {/* Do's */}
          <section className="bg-card border border-border rounded-2xl p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-green-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Check className="h-5 w-5" />
              Do's - Good Practices
            </h2>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
                <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="text-green-800">Be Honest</strong>
                  <p className="text-green-700 text-sm">Provide accurate descriptions, real photos, and truthful information in your listings.</p>
                </div>
              </li>
              <li className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
                <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="text-green-800">Be Respectful</strong>
                  <p className="text-green-700 text-sm">Communicate professionally and treat all community members with respect.</p>
                </div>
              </li>
              <li className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
                <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="text-green-800">Be Safe</strong>
                  <p className="text-green-700 text-sm">Meet in public places for transactions and verify items before purchasing.</p>
                </div>
              </li>
              <li className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
                <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="text-green-800">Respond Promptly</strong>
                  <p className="text-green-700 text-sm">Reply to messages in a timely manner and keep your listings up to date.</p>
                </div>
              </li>
              <li className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
                <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="text-green-800">Report Issues</strong>
                  <p className="text-green-700 text-sm">If you see suspicious activity, report it through our support system.</p>
                </div>
              </li>
            </ul>
          </section>

          {/* Don'ts */}
          <section className="bg-card border border-border rounded-2xl p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-red-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <X className="h-5 w-5" />
              Don'ts - Prohibited Activities
            </h2>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 p-3 bg-red-50 rounded-xl">
                <X className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="text-red-800">No Scams or Fraud</strong>
                  <p className="text-red-700 text-sm">Do not deceive or defraud other users. Scammers will be permanently banned.</p>
                </div>
              </li>
              <li className="flex items-start gap-3 p-3 bg-red-50 rounded-xl">
                <X className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="text-red-800">No Illegal Items</strong>
                  <p className="text-red-700 text-sm">Do not list weapons, drugs, stolen goods, counterfeit items, or anything illegal in Canada.</p>
                </div>
              </li>
              <li className="flex items-start gap-3 p-3 bg-red-50 rounded-xl">
                <X className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="text-red-800">No Spam</strong>
                  <p className="text-red-700 text-sm">Do not post duplicate listings, irrelevant content, or promotional spam.</p>
                </div>
              </li>
              <li className="flex items-start gap-3 p-3 bg-red-50 rounded-xl">
                <X className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="text-red-800">No Harassment</strong>
                  <p className="text-red-700 text-sm">Do not harass, threaten, or intimidate other users. Be respectful in all communications.</p>
                </div>
              </li>
              <li className="flex items-start gap-3 p-3 bg-red-50 rounded-xl">
                <X className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="text-red-800">No Hate Speech</strong>
                  <p className="text-red-700 text-sm">Content promoting discrimination, racism, or hate is strictly prohibited.</p>
                </div>
              </li>
            </ul>
          </section>

          {/* Consequences */}
          <section className="bg-orange-50 border border-orange-200 rounded-2xl p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-orange-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <AlertTriangle className="h-5 w-5" />
              Consequences for Violations
            </h2>
            <p className="text-orange-700 leading-relaxed mb-4">
              Violations of these rules may result in:
            </p>
            <ul className="space-y-2 text-orange-700">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 flex-shrink-0"></span>
                <span>Removal of the offending listing</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 flex-shrink-0"></span>
                <span>Warning notification to your account</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 flex-shrink-0"></span>
                <span>Temporary suspension of your account</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 flex-shrink-0"></span>
                <span>Permanent ban from HabeshaTown</span>
              </li>
            </ul>
          </section>

          {/* Report */}
          <section className="bg-card border border-border rounded-2xl p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Report a Violation
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              If you encounter a user or listing that violates these rules, please report it through our{' '}
              <Link to="/support" className="text-primary hover:underline">Help & Support</Link> page. 
              We take all reports seriously and will investigate promptly.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
